import { CodeBlock, FormattedDateTime, Heading, Icon, Paragraph, Strong } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import type { SqlImportDatabase } from '@/services/db/sql-import-database/entity';
import { ImportSummary, sqlImportDatabaseLabel, sqlImportSyncTargetKind } from '@/features/sql-sync';
import { isEmptyString } from '@/utils/string';
import { epochNowInSeconds } from '@/utils/time';
import { sqlImportDuration } from '@/services/db/sql-import/entity';
import type { SqlImportDraft } from '../draft';

type ReviewStepProps = {
  workspaceId: string;
  draft: SqlImportDraft;
  database: SqlImportDatabase | undefined;
  syncTarget: string;
  segment: { id: string; name: string } | null;
};

type ReviewHeadingProps = {
  section: string;
  children: string;
};

function ReviewHeading({ section, children }: ReviewHeadingProps) {
  return (
    <Heading level="3" text="product-h4" mt="300" mb="100" data-test-create-sql-import-review-section={section}>
      {children}
    </Heading>
  );
}

/** The last step: everything the sync will do, read back before it is created. */
export function ReviewStep({ workspaceId, draft, database, syncTarget, segment }: ReviewStepProps) {
  const { track } = useAnalytics();
  const relationshipQuery = draft.relationship?.query;
  const isScheduled = (draft.start_at ?? 0) > epochNowInSeconds();

  usePageViewTracking(() => {
    const syncTargetKind = sqlImportSyncTargetKind(syncTarget);
    if (isEmptyString(relationshipQuery)) {
      void track('sqlImports', 'reviewImport', { query: draft.query ?? '', 'Sync Target': syncTargetKind });
      return;
    }
    void track('sqlImports', 'reviewImport', {
      query: draft.query ?? '',
      'Sync Target': syncTargetKind,
      'Relationship Query': relationshipQuery,
    });
  });

  return (
    <div>
      <Heading level="2" text="product-h3" mb="100">
        Review and sync your import
      </Heading>

      <ReviewHeading section="sync">Sync</ReviewHeading>
      <Paragraph mb="200">
        Every {sqlImportDuration({ interval: draft.interval ?? 0 })},{' '}
        <ImportSummary
          workspaceId={workspaceId}
          sqlImport={draft}
          syncTarget={syncTarget}
          segment={segment}
          withIdentifyBy
        />
      </Paragraph>

      {isScheduled ? (
        <>
          <ReviewHeading section="start">Start</ReviewHeading>
          <Paragraph>
            Sync is scheduled to start on{' '}
            <Strong>
              <FormattedDateTime date={(draft.start_at ?? 0) * 1000} format="full_with_time" />
            </Strong>
            .
          </Paragraph>
        </>
      ) : null}

      <ReviewHeading section="database">Source</ReviewHeading>
      <Paragraph mb="200">
        Sync {syncTarget.toLowerCase()} from <Icon name="database" display="inline-block" />{' '}
        <Strong>{database == null ? '' : sqlImportDatabaseLabel(database)}</Strong>
        {isEmptyString(draft.location) ? null : (
          <>
            {' '}
            in <Strong>{draft.location}</Strong>
          </>
        )}
      </Paragraph>

      <ReviewHeading section="query">Query</ReviewHeading>
      <CodeBlock code={draft.query ?? ''} language="sql" withCopyButton={false} />

      {isEmptyString(relationshipQuery) ? null : (
        <>
          <ReviewHeading section="relationship-query">Relationships query</ReviewHeading>
          <CodeBlock code={relationshipQuery} language="sql" withCopyButton={false} />
        </>
      )}
    </div>
  );
}
